import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';

/**
 * ImageUploader - Componente riutilizzabile per upload immagini con supporto fotocamera
 * 
 * Props:
 * - currentImage: URL dell'immagine attuale (opzionale)
 * - onImageChange: Callback chiamato con la nuova immagine (base64 o URL)
 * - onImageRemove: Callback per rimuovere l'immagine
 * - aspectRatio: "square" | "landscape" | "portrait" (default: "square")
 * - maxSizeMB: Dimensione massima in MB (default: 2)
 * - label: Etichetta del campo
 * - disabled: Se disabilitato
 */
export default function ImageUploader({
  currentImage,
  onImageChange,
  onImageRemove,
  aspectRatio = 'square',
  maxSizeMB = 2,
  label = 'Immagine',
  disabled = false,
  className = ''
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const aspectRatioClass = {
    square: 'aspect-square',
    landscape: 'aspect-video',
    portrait: 'aspect-[3/4]'
  }[aspectRatio];

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine valido');
      return;
    }

    // Validate file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      toast.error(`L'immagine deve essere inferiore a ${maxSizeMB}MB`);
      return;
    }

    setIsUploading(true);
    try {
      // Compress and convert to base64
      const compressedBase64 = await compressImage(file, 800, 0.8);
      setPreviewUrl(compressedBase64);
      onImageChange(compressedBase64);
      setIsDialogOpen(false);
      toast.success('Immagine caricata');
    } catch (error) {
      console.error('Errore upload:', error);
      toast.error('Errore durante il caricamento');
    } finally {
      setIsUploading(false);
    }
  };

  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Scale down if needed
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    if (onImageRemove) {
      onImageRemove();
    } else {
      onImageChange('');
    }
    toast.success('Immagine rimossa');
  };

  const displayImage = previewUrl || currentImage;

  return (
    <div className={className}>
      <label className="text-sm font-medium mb-2 block">{label}</label>
      
      <div className={`relative ${aspectRatioClass} w-full max-w-[200px] border-2 border-dashed rounded-lg overflow-hidden bg-muted/30 hover:bg-muted/50 transition-colors`}>
        {displayImage ? (
          <>
            <img 
              src={displayImage} 
              alt={label}
              className="w-full h-full object-cover"
            />
            {!disabled && (
              <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setIsDialogOpen(true)}
                  data-testid="change-image-btn"
                >
                  <Camera className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleRemove}
                  data-testid="remove-image-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setIsDialogOpen(true)}
            disabled={disabled}
            className="w-full h-full flex flex-col items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            data-testid="add-image-btn"
          >
            <ImageIcon className="w-8 h-8 mb-2" />
            <span className="text-xs">Aggiungi immagine</span>
          </button>
        )}
      </div>

      {/* Dialog per scegliere fonte immagine */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Carica Immagine</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preview se presente */}
            {displayImage && (
              <div className={`${aspectRatioClass} w-full max-w-[300px] mx-auto rounded-lg overflow-hidden border`}>
                <img 
                  src={displayImage} 
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Opzioni upload */}
            <div className="grid grid-cols-2 gap-4">
              {/* Scatta foto */}
              <Button
                variant="outline"
                className="h-24 flex-col gap-2"
                onClick={() => cameraInputRef.current?.click()}
                disabled={isUploading}
                data-testid="camera-btn"
              >
                {isUploading ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : (
                  <>
                    <Camera className="w-8 h-8" />
                    <span className="text-sm">Scatta Foto</span>
                  </>
                )}
              </Button>

              {/* Carica da galleria */}
              <Button
                variant="outline"
                className="h-24 flex-col gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                data-testid="upload-btn"
              >
                {isUploading ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-8 h-8" />
                    <span className="text-sm">Carica File</span>
                  </>
                )}
              </Button>
            </div>

            {/* URL immagine */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">oppure</span>
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Incolla URL immagine..."
                className="flex-1 px-3 py-2 text-sm border rounded-md"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const url = e.target.value.trim();
                    if (url) {
                      onImageChange(url);
                      setIsDialogOpen(false);
                      toast.success('Immagine caricata da URL');
                    }
                  }
                }}
                data-testid="image-url-input"
              />
              <Button
                variant="secondary"
                onClick={(e) => {
                  const input = e.target.closest('.flex').querySelector('input');
                  const url = input?.value?.trim();
                  if (url) {
                    onImageChange(url);
                    setIsDialogOpen(false);
                    toast.success('Immagine caricata da URL');
                  }
                }}
              >
                Usa URL
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Formati supportati: JPG, PNG, WebP • Max {maxSizeMB}MB
            </p>
          </div>

          {/* Input nascosti per file e camera */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="camera-input"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
